import React, { useState, useEffect } from 'react';
import { getApiErrorMessage } from '../../utils/apiError';
import authService from '../../services/authService';

const ChangePasswordForm = ({ onSuccess }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Use effect to prevent navigation
  useEffect(() => {
    // Keep us on this page
    if (success) {
      const preventNavigation = () => {
        window.history.pushState({}, '', window.location.pathname);
        console.log("Preventing navigation, staying on password page");
      };
      
      // Call it once
      preventNavigation();
      
      // Set up listener for history changes
      window.addEventListener('popstate', preventNavigation);
      
      // Clean up
      return () => {
        window.removeEventListener('popstate', preventNavigation);
      };
    }
  }, [success]);

  const containerStyle = {
    maxWidth: '400px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  };
  
  const headingStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
    textAlign: 'center'
  };
  
  const formStyle = {
    marginTop: '1.5rem'
  };
  
  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    marginBottom: '1rem'
  };
  
  const buttonStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    fontWeight: '500',
    cursor: 'pointer'
  };
  
  const errorStyle = {
    color: '#dc2626',
    fontSize: '0.875rem',
    marginBottom: '1rem'
  };
  
  const successStyle = {
    backgroundColor: '#ecfdf5', 
    padding: '1rem', 
    borderRadius: '0.375rem',
    marginBottom: '1rem' 
  };

  // Handler for direct form submission - avoids react-router navigations
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setError('');
    setSuccess(false);
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get admin token from authService
      const adminToken = authService.getAdminToken();
      
      if (!adminToken) {
        setError('You need to be logged in to change your password');
        setIsLoading(false);
        return;
      }
      
      // Use native fetch to bypass all axios interceptors
      const response = await fetch(`${window.location.origin}/api/admin-auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-token': 'cnxn_admin_token',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ 
          currentPassword, 
          newPassword 
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Call the success callback
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess();
        }
        
        // Force stay on this page
        window.history.pushState({}, '', window.location.pathname);
        console.log("Forcing to stay on change password page");
      }
    } catch (err) {
      console.error('Change password error:', err);
      setError('Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div style={containerStyle}>
      <h2 style={headingStyle}>Change Password</h2>
      
      {success && (
        <div style={successStyle}>
          <h3 style={{ 
            fontWeight: '500', 
            color: '#065f46',
            marginBottom: '0.5rem'
          }}>
            Password changed successfully
          </h3>
          <p style={{ color: '#047857', fontSize: '0.875rem' }}>
            Your password has been updated.
          </p>
        </div>
      )}
      
      <form style={formStyle} onSubmit={handleSubmit}>
        {error && (
          <div style={errorStyle}>
            {error}
          </div>
        )}
        
        <div>
          <input
            id="currentPassword"
            name="currentPassword"
            type="password"
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={inputStyle}
            required
          />
        </div>
        
        <div>
          <input
            id="newPassword"
            name="newPassword"
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={inputStyle}
            required
          />
        </div>
        
        <div>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={inputStyle}
            required
          />
        </div>
        
        <div>
          <button
            type="submit"
            disabled={isLoading}
            style={buttonStyle}
          >
            {isLoading ? 'Changing Password...' : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordForm;
